import axios, { HttpStatusCode } from 'axios'
import { redirectToLogoutPage } from '@/utils/routing'

export const HEADER_SKIP_AUTOEXTEND = 'X-Skip-Autoextend'

/**
 * @class ApiClient
 * This class represents API client used for back-end server communication.
 * It uses "axios" http client for sending ajax requests.
 * @see https://github.com/axios/axios
 */
export default class ApiClient {
  /**
   * Create new ApiClient instance.
   *
   * @param  {Object} options
   */
  constructor (options) {
    this.axios = axios.create({
      headers: { 'Accept': 'application/json' }
    })

    this.settings = Object.assign({
      resourceSeparator: '/',
      baseUrl: 'localhost'
    }, options)
  }

  /**
   * Create "request" config object.
   * @see  https://github.com/axios/axios
   *
   * @param  {String} method
   * @param  {String|Array} resource
   * @param  {Object} data
   * @return {Object}
   */
  createRequest (method, resource, data, options) {
    const request = {
      method,
      url: `${this.settings.baseUrl}/${this.createResourcePath(resource)}`,
      paramsSerializer: this.serializeParams,
      withCredentials: true,
      ...options
    }

    request[(method === 'GET') ? 'params' : 'data'] = data

    return request
  }

  /**
   * Serialize request params (string/array)
   *
   * @return {Object}
   */
  serializeParams (params) {
    const serializedParams = new URLSearchParams()

    for (const key in params) {
      if (Array.isArray(params[key])) {
        params[key].forEach(value => {
          if (value != null && value !== '') {
            serializedParams.append(key, value)
          }
        })
      } else if (params[key] != null && params[key] !== '') {
        serializedParams.append(key, params[key])
      }
    }

    return serializedParams
  }

  /**
   * Send API request.
   * @see this._sendRequest()
   *
   * @param  {String} method
   * @param  {Array|String} resource
   * @param  {Object} [data]
   * @return {Promise}
   */
  sendRequest (method, resource, data, options) {
    return this.axios(this.createRequest(method, resource, data, options))
      .then(response => response.data)
      .catch((error) => {
        if (error.response != null && error.response.status === HttpStatusCode.Unauthorized) {
          // Return unresolved Promise to prevent processing of the following registered handlers.
          return new Promise(() => void redirectToLogoutPage())
        }

        throw error
      })
  }

  /**
   * Send unauthorized API request.
   * @see this._sendRequest()
   *
   * @param  {String} method
   * @param  {Array|String} resource
   * @param  {Object} [data]
   * @return {Promise}
   */
  sendUnauthorizedRequest (method, resource, data, options) {
    return this.axios(this.createRequest(method, resource, data, options))
      .then(response => response.data)
  }

  /**
   * Make resource path string from array or string.
   *
   * Examples:
   *   this.createResourcePath(['user', 1]) === 'user/1';
   *   this.createResourcePath('user') === 'user';
   *
   * @param  {String|Array} resource
   * @return {String}
   */
  createResourcePath (resource) {
    if (Array.isArray(resource)) {
      return resource.join(this.settings.resourceSeparator)
    }

    return resource
  }

  /**
   * Register response interceptor.
   * @see Axios interceptors in Google for more details
   *
   * @param {Function} interceptor
   * @return {ApiClient}
   */
  registerResponseInterceptor (Interceptor) {
    const interceptor = new Interceptor(this)

    this.axios.interceptors.response.use(
      interceptor.interceptSuccess.bind(interceptor),
      interceptor.interceptError.bind(interceptor)
    )

    return this
  }
}
